import { GenericError } from '../../../errors/shared/genericError.js';
import { AbortError, UnknownError } from '../../../errors/shared/base.js';
import { HttpTimeoutError } from '../../../errors/shared/http.js';
import { Err, Ok } from '../../../result/shared/result.js';
import { iteratorFilterMap } from '../../../iterator/shared/iterator.js';
import { None, Some } from '../../../option/shared/option.js';
import { HttpResponse } from '../Response/Response.js';
import { urlToLog } from '../url.js';
import { DEFAULT_HTTP_TIMEOUT, BASE_CLIENT_NAMESPACE } from '../const.js';
import { withResolvers } from '../../../utils/shared/promise.js';
import { abortSignalAny } from '../../../utils/shared/abort.js';

const NAMESPACE = BASE_CLIENT_NAMESPACE + '.xhr';
const DEFAULT_XHR_RESPONSE_TYPE = 'arraybuffer';
/**
 * Создает объект запроса на основе {@linkcode XMLHttpRequest}
 *
 * @export
 * @class Request
 * @implements {IRequest<Res>}
 * @template Req - тип тела запроса
 * @template Res - тип тела ответа
 */
class Request {
    reqConfig;
    timeoutAC = new AbortController();
    constructor(reqConfig) {
        this.reqConfig = reqConfig;
    }
    async send() {
        const { resolve, promise } = withResolvers();
        const { url, withCredentials, method, headers, body, responseContentType, timeout = DEFAULT_HTTP_TIMEOUT, requestServerName, signal: userSignal, } = this.reqConfig;
        const signal = userSignal
            ? (AbortSignal.any || abortSignalAny)([userSignal, this.timeoutAC.signal])
            : this.timeoutAC.signal;
        if (signal.aborted) {
            return Err(signal.reason);
        }
        try {
            const request = new XMLHttpRequest();
            signal.addEventListener('abort', () => {
                if (request.readyState !== request.DONE) {
                    request.abort();
                }
            });
            request.addEventListener('abort', ev => {
                if (signal.aborted) {
                    resolve(Err(signal.reason));
                }
                else {
                    resolve(Err(new AbortError({
                        message: `Request was aborted by unknown reason ${urlToLog(url)}`,
                        domain: NAMESPACE + `.request-abort.${requestServerName}`,
                        details: {
                            lengthComputable: ev.lengthComputable,
                            loaded: ev.loaded,
                            total: ev.total,
                        },
                    })));
                }
            });
            request.addEventListener('error', ev => {
                resolve(Err(new UnknownError({
                    message: `Microservice ${requestServerName} api ${urlToLog(url)} request error`,
                    domain: NAMESPACE + `.request-error.${requestServerName}`,
                    details: {
                        readyState: request.readyState,
                        status: request.status,
                        statusText: request.statusText,
                        lengthComputable: ev.lengthComputable,
                        loaded: ev.loaded,
                        total: ev.total,
                    },
                })));
            });
            request.addEventListener('timeout', ev => {
                resolve(Err(new HttpTimeoutError({
                    timeout,
                    url: urlToLog(url).toString(),
                    domain: NAMESPACE + `.request-timeout.${requestServerName}`,
                    details: {
                        readyState: request.readyState,
                        lengthComputable: ev.lengthComputable,
                        loaded: ev.loaded,
                        total: ev.total,
                    },
                })));
            });
            request.addEventListener('load', () => resolve(Ok.Empty));
            request.open(method, url, true);
            if (headers) {
                for (const [name, value = ''] of headers) {
                    // TODO: Значение заголовка может быть массивом
                    request.setRequestHeader(name, value);
                }
            }
            request.timeout = timeout;
            request.withCredentials = !!withCredentials;
            request.responseType = DEFAULT_XHR_RESPONSE_TYPE;
            request.send(body);
            const result = await promise;
            if (result.isErr()) {
                return result;
            }
            const responseData = request.response === null ? null : makeEmptyBodyIfArrayBufferEmpty(request.response);
            /**
             * Переводим строку (в формате {@link [CRLF](https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/getAllResponseHeaders)})
             * заголовков ответа в [string, string][]
             */
            const responseHeaders = Array.from(iteratorFilterMap(request.getAllResponseHeaders().split('\r\n'), rawHeader => {
                if (rawHeader === '') {
                    return None();
                }
                else {
                    const [header, value] = rawHeader.split(': ');
                    return Some([header, value]);
                }
            }));
            const response = new Response(responseData, {
                status: request.status,
                statusText: request.statusText,
                headers: responseHeaders,
            });
            const httpResponse = new HttpResponse(response, {
                requestServerName,
                timeout,
                responseContentType,
                url,
            });
            const bodyResult = await httpResponse.compileBody();
            if (bodyResult.isErr()) {
                return bodyResult;
            }
            return Ok(httpResponse);
        }
        catch (error) {
            if (GenericError.isLike(error)) {
                return Err(error);
            }
            return Err(new UnknownError({
                message: `Unknown XHR request exception ${urlToLog(url).toString()}`,
                domain: NAMESPACE + `.unknown-error.${requestServerName}`,
                details: error,
            }));
        }
    }
}
function makeEmptyBodyIfArrayBufferEmpty(bodyBuffer) {
    return bodyBuffer.byteLength > 0 ? bodyBuffer : null;
}

export { Request };
//# sourceMappingURL=Request.js.map
